import type { Condition } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/conditions';
import type TriggerGeofence from '@cobliteam/api-lib/src/dashboard/alerts/definitions/geofences';
import type { TriggerRoadSpeedSeverity } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/roadSpeed';
import type { Rule } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { TriggerRecord } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/triggers';
import type { TriggerUserPreferences } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/users';
import type TriggerVehicleGroup from '@cobliteam/api-lib/src/dashboard/alerts/definitions/vehicleGroups';
import type TriggerVehicle from '@cobliteam/api-lib/src/dashboard/alerts/definitions/vehicles';
import { formatDateTZ } from '@cobliteam/utils/date';
import { DateOption, dateOptions } from './dateOption';
import type { FormValues } from './triggerFormSchema';

interface TriggerFormValues {
  dateOption: DateOption;
  vehicleGroups: TriggerVehicleGroup[];
  vehicles: TriggerVehicle[];
  sendPushNotification: boolean;
  geofences: TriggerGeofence[];
  name?: string;
  rule?: Rule;
  condition?: Condition | null;
  startTime?: string;
  endTime?: string;
  minRoadSpeedSeverity?: TriggerRoadSpeedSeverity;
  notifiedUsersChannels: TriggerUserPreferences[];
  requestVideo: boolean;
}

const DEFAULT_TRIGGER_FORM_VALUES: TriggerFormValues = {
  vehicles: [],
  vehicleGroups: [],
  geofences: [],
  sendPushNotification: true,
  dateOption: DateOption.EVERYDAY,
  condition: null,
  minRoadSpeedSeverity: undefined,
  notifiedUsersChannels: [],
  requestVideo: false,
};

const mapFormValuesToTriggerRecord = (data: FormValues): TriggerRecord => {
  const { name, condition, minRoadSpeedSeverity, geofences, vehicleGroups, dateOption, hour, sendPushNotification, vehicles, notifiedUsersChannels, requestVideo } = data;
  const { start, end } = hour || {};

  const [sHour, sMin, sSec] = start?.split(':').map((time) => Number.parseInt(time, 10)) ?? [0, 0, 0];
  const [eHour, eMin, eSec] = end?.split(':').map((time) => Number.parseInt(time, 10)) ?? [23, 59, 59];

  const startTimeInUTC = formatDateTZ(new Date().setHours(sHour, sMin, sSec), 'HH:mm:ss', 'UTC');
  const endTimeInUTC = formatDateTZ(new Date().setHours(eHour, eMin, eSec), 'HH:mm:ss', 'UTC');

  if (!(name && condition)) {
    throw new Error('Trigger name and condition are required');
  }

  return {
    name,
    startTime: startTimeInUTC,
    endTime: endTimeInUTC,
    conditionId: condition.id,
    daysOfWeek: dateOptions[dateOption],
    allowNotifications: sendPushNotification,
    vehicleIds: vehicles?.map((vehicle) => vehicle.id),
    geofenceIds: geofences?.map((geofence) => geofence.id),
    vehicleGroupIds: vehicleGroups?.map((vehicleGroup) => vehicleGroup.id),
    minRoadSpeedSeverity,
    notifiedUsersChannels: sendPushNotification ? notifiedUsersChannels : [],
    requestVideo,
  };
};

export { mapFormValuesToTriggerRecord, DEFAULT_TRIGGER_FORM_VALUES };
export type { TriggerFormValues, TriggerGeofence };
